import { runInAction } from 'mobx';
import { AxiosResponse } from 'axios';

import { UnreachableFunction, notUndefined } from 'ui/base/types';

import Time from 'services/base/time';
import { ResultKind } from 'services/base/types';
import { generateUUID } from 'services/base/uuid';
import { createRequestString, getResponseBytes, http, isHttpError } from 'services/base/http';

import {
  GetScheduleResponse,
  GetSchedulesResponse,
  ScheduleDayFlag,
  ScheduleHostIdList,
  ScheduleObject,
  ScheduleObjectMap,
  ScheduleObjectMapTuple,
  SchedulePolicyCategory,
  SchedulePolicyKind,
  SchedulePolicyObject,
  SchedulePolicyObjectMap,
  SchedulePolicyObjectMapTuple,
  ScheduleObject as ScheduleProto,
  SetSchedulesRequest,
} from './protos/schedules_pb';

import {
  IPangolinDomain,
  Schedule,
  ScheduleMap,
  SchedulePolicy,
  SchedulePolicyMap,
} from './types';

type PartialScheduleWithID = Pick<Schedule, 'uuid'> & Partial<Schedule>;

export type ScheduleUpdateResult =
  | { kind: ResultKind.OK; schedule: PartialScheduleWithID }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number; }
  | { kind: ResultKind.USER_ERROR; status?: number;
      nameError?: string;
      daysError?: string; };

export type ScheduleDeleteResult =
  | { kind: ResultKind.OK }
  | { kind: ResultKind.SYSTEM_ERROR; status?: number }
  | { kind: ResultKind.USER_ERROR; status?: number };

export class SchedulesService {
  static async fetch(domain: IPangolinDomain, id: string, save = true): Promise<Schedule | undefined> {
    const httpResp = await http.get(`/pangolin/${domain.id}/schedules/${id}`);
    return runInAction(() => {
      const remoteSchedule = parseScheduleResponse(httpResp);
      if (remoteSchedule == null) return undefined;

      if (save) {
        const localSchedule = domain.schedules.get(id);
        if (localSchedule == null) {
          domain.schedules.set(remoteSchedule.uuid, remoteSchedule);
        } else {
          Object.assign(localSchedule, remoteSchedule);
        }
      }

      return remoteSchedule;
    });
  }

  static async fetchAll(domain: IPangolinDomain, save = true): Promise<ScheduleMap> {
    const httpResp = await http.get(`/pangolin/${domain.id}/schedules`);
    const remoteSchedules = parseSchedulesResponse(httpResp);
    return runInAction(() => {
      return runInAction(() => {
        if (save) domain.schedules = remoteSchedules;
        return remoteSchedules;
      });
    });
  }

  static async update(domain: IPangolinDomain, schedule: PartialScheduleWithID): Promise<ScheduleUpdateResult> {
    const request = createSetSchedulesRequest(domain, schedule);
    try {
      await http.put(`/pangolin/${domain.id}/schedules`, createRequestString(request));
      const remoteSchedule = await this.fetch(domain, schedule.uuid, false);
      return runInAction(() => {
        if (remoteSchedule != null) {
          const remotePartial = updatePartialFromComplete(schedule, remoteSchedule);
          updatePersistentFromPartial(domain, remotePartial);
          return { kind: ResultKind.OK, schedule: remotePartial };
        }

        return { kind: ResultKind.USER_ERROR, status: 404 };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }

  static async delete(domain: IPangolinDomain, schedule: Schedule): Promise<ScheduleDeleteResult> {
    const request = createDeleteSchedulesRequest(schedule);
    try {
      await http.put(`/pangolin/${domain.id}/schedules`, createRequestString(request));
      return runInAction(() => {
        domain.schedules.delete(schedule.uuid);
        return { kind: ResultKind.OK };
      });
    } catch (error) {
      const status = isHttpError(error) ? error.response?.status : undefined;
      const kind = status && status < 500 ? ResultKind.USER_ERROR : ResultKind.SYSTEM_ERROR;
      return { kind, status };
    }
  }
}

function parseScheduleResponse(httpResp: AxiosResponse): Schedule | undefined {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetScheduleResponse.deserializeBinary(rawBytes);
  const itemProto = rootProto.getSchedule();
  return scheduleFromProto(itemProto);
}

function parseSchedulesResponse(httpResp: AxiosResponse): ScheduleMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = GetSchedulesResponse.deserializeBinary(rawBytes);
  const listProto = rootProto.getSchedules().getItemsList();
  const scheduleObjects = listProto.map((p) => scheduleFromProto(p.getData()));
  return new ScheduleMap(scheduleObjects.filter(notUndefined).map((s) => [s.uuid, s]));
}

function updatePersistentFromPartial(domain: IPangolinDomain, partial: PartialScheduleWithID): void {
  let target = domain.schedules.get(partial.uuid);
  if (target == null) {
    target = scheduleCreate(partial);
    domain.schedules.set(partial.uuid, target);
    target = domain.schedules.get(partial.uuid);
    if (target == null) return;
  }
  Object.assign(target, partial);
}

function updatePartialFromComplete(partial: PartialScheduleWithID, complete: Schedule): PartialScheduleWithID {
  const updated: PartialScheduleWithID = Object.keys(partial).reduce((result, key) => {
    if (Object.hasOwn(complete, key)) {
      (result as Record<string, unknown>)[key] = (complete as Record<string, unknown>)[key];
    }
    return result;
  }, { uuid: partial.uuid });

  return updated;
}

export function scheduleCreate(partial?: PartialScheduleWithID): Schedule {
  const schedule: Schedule = {
    uuid: generateUUID(),
    name: '',
    enabled: true,
    start: new Time(0, 0, 0),
    end: new Time(0, 0, 0),
    applyToAllHosts: false,
    applyAllPolicies: false,
    days: {
      monday: false,
      tuesday: false,
      wednesday: false,
      thursday: false,
      friday: false,
      saturday: false,
      sunday: false,
    },
    hostIDs: [],
    policies: new SchedulePolicyMap(),
  };

  if (partial != null) {
    Object.assign(schedule, partial);
  }

  return schedule;
}

export function scheduleFromProto(proto: ScheduleObject | undefined): Schedule | undefined {
  if (proto === undefined) return;

  const uuid = proto.getUuid();

  if (uuid === undefined) return;

  const policies = createSchedulePolicies(proto.getPolicies());

  return {
    uuid, name: proto.getName() ?? '',
    enabled: proto.getEnabled() ?? false,
    start: Time.parseLocal(proto.getStart()),
    end: Time.parseLocal(proto.getEnd()),
    days: {
      monday: proto.getDays()?.getMonday() ?? false,
      tuesday: proto.getDays()?.getTuesday() ?? false,
      wednesday: proto.getDays()?.getWednesday() ?? false,
      thursday: proto.getDays()?.getThursday() ?? false,
      friday: proto.getDays()?.getFriday() ?? false,
      saturday: proto.getDays()?.getSaturday() ?? false,
      sunday: proto.getDays()?.getSunday() ?? false,
    },
    applyToAllHosts: proto.getHostAll() ?? false,
    applyAllPolicies: proto.getPolicyAll() ?? false,
    hostIDs: createHostIDs(proto),
    policies,
  };
}

function createHostIDs(proto: ScheduleProto): string[] {
  const hosts = proto.getHosts()?.getItemsList();
  return hosts != null ? hosts : [];
}

function createSchedulePolicy(proto: SchedulePolicyObjectMapTuple): SchedulePolicy | undefined {
  const id = proto.getId();
  const data = proto.getData();
  if (id == null || data == null) {
    return undefined;
  }
  const kind = data.getKind();
  switch(kind) {
    case SchedulePolicyKind.P_UNKNOWN:
    case undefined:
      return {
        kind: SchedulePolicyKind.P_UNKNOWN,
        uuid: id,
      };
    case SchedulePolicyKind.P_CATEGORY:
      return {
        kind: SchedulePolicyKind.P_CATEGORY,
        uuid: id,
        category: data.getCategory() ?? SchedulePolicyCategory.C_UNKNOWN,
      };
    case SchedulePolicyKind.P_CUSTOM:
      return {
        kind: SchedulePolicyKind.P_CUSTOM,
        uuid: id,
        custom: data.getCustom() ?? '',
      };
    default:
      return UnreachableFunction(kind);
  }
}

function createSchedulePolicies(protos: SchedulePolicyObjectMap | undefined): SchedulePolicyMap {
  return new SchedulePolicyMap(protos?.getItemsList()
                                      .map(createSchedulePolicy)
                                      .filter(notUndefined)
                                      .map((i) => [i.uuid, i]));
}

function createSetSchedulesRequest(domain: IPangolinDomain, schedule: PartialScheduleWithID): SetSchedulesRequest {
  // Lots of roundabout data bashing because of John's message definitions
  const proto = new ScheduleProto();
  if (schedule.name != null) {
    proto.setName(schedule.name);
  }
  if (schedule.enabled != null) {
    proto.setEnabled(schedule.enabled);
  }
  if (schedule.start != null) {
    proto.setStart(schedule.start.toLocalString());
  }
  if (schedule.end != null) {
    proto.setEnd(schedule.end.toLocalString());
  }
  if (schedule.applyToAllHosts != null) {
    proto.setHostAll(schedule.applyToAllHosts);
  }
  if (schedule.applyAllPolicies != null) {
    proto.setPolicyAll(schedule.applyAllPolicies);
  }

  if (schedule.days != null) {
    const days = new ScheduleDayFlag();
    days.setMonday(schedule.days.monday);
    days.setTuesday(schedule.days.tuesday);
    days.setWednesday(schedule.days.wednesday);
    days.setThursday(schedule.days.thursday);
    days.setFriday(schedule.days.friday);
    days.setSaturday(schedule.days.saturday);
    days.setSunday(schedule.days.sunday);
    proto.setDays(days);
  }

  if (schedule.hostIDs != null) {
    const hostIDs = new ScheduleHostIdList();
    schedule.hostIDs.forEach((id) => hostIDs.addItems(id));
    proto.setHosts(hostIDs);
  }

  if (schedule.policies != null) {
    // TypeScript doesn't handle this kind of type narrowing well so we have to
    // declare it manually.
    const narrowed = schedule as PartialScheduleWithIDAndPolicies;

    // Add all the new policies
    const policies = new SchedulePolicyObjectMap();
    narrowed.policies.forEach((policy) => {
      const tuple = new SchedulePolicyObjectMapTuple();
      tuple.setId(policy.uuid);

      const data = new SchedulePolicyObject();
      switch (policy.kind) {
        case SchedulePolicyKind.P_UNKNOWN:
          data.setKind(policy.kind);
          break;
        case SchedulePolicyKind.P_CATEGORY:
          data.setKind(policy.kind);
          data.setCategory(policy.category);
          break;
        case SchedulePolicyKind.P_CUSTOM:
          data.setKind(policy.kind);
          data.setCustom(policy.custom);
          break;
        default:
          UnreachableFunction(policy);
      }
      tuple.setData(data);

      policies.addItems(tuple);
    });

    // Find all the old ones and mark them for deletion
    const deletedPolicyIDs = getDeletedPolicyIDs(domain, narrowed);
    deletedPolicyIDs.forEach((id) => {
      const tuple = new SchedulePolicyObjectMapTuple();
      tuple.setId(id);
      tuple.setData(undefined);
      policies.addItems(tuple);
    });

    proto.setPolicies(policies);
  }

  const tuple = new ScheduleObjectMapTuple();
  tuple.setId(schedule.uuid);
  tuple.setData(proto);

  const schedules = new ScheduleObjectMap();
  schedules.addItems(tuple);

  const request = new SetSchedulesRequest();
  request.setSchedules(schedules);

  return request;
}

type PartialScheduleWithIDAndPolicies = Pick<Schedule, 'uuid' | 'policies'> & Partial<Schedule>;

function getDeletedPolicyIDs(domain: IPangolinDomain, schedule: PartialScheduleWithIDAndPolicies): string[] {
  const old = domain.schedules.get(schedule.uuid);

  if (old != null) {
    const newIDs: Map<string, true> = new Map();
    schedule.policies.forEach((p) => newIDs.set(p.uuid, true));
    const deletedIDs = old.policies.map(p => p.uuid).filter(id => !newIDs.has(id));
    return deletedIDs;
  }

  return [];
}

export function createDeleteSchedulesRequest(schedule: Schedule): SetSchedulesRequest {
  const tuple = new ScheduleObjectMapTuple();
  tuple.setId(schedule.uuid);
  tuple.setData(undefined);

  const schedules = new ScheduleObjectMap();
  schedules.addItems(tuple);

  const request = new SetSchedulesRequest();
  request.setSchedules(schedules);

  return request;
}
